import AddIcon from '@mui/icons-material/Add'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Switch from '@mui/material/Switch'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMySites } from '../api/hooks'
import {
  useCreateDailyReport,
  useDailyReports,
  type ReportStatus,
  type Shift,
  type Weather,
} from '../api/execution'
import { useAuth } from '../auth/useAuth'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { PageHeader } from '../components/PageHeader'
import { ReportStatusChip } from '../components/ReportStatusChip'

const STATUSES: ReportStatus[] = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'APPROVED',
  'REJECTED',
  'REVISED',
]

const WEATHER: Weather[] = ['CLEAR', 'CLOUDY', 'RAIN', 'HEAVY_RAIN', 'STORM', 'EXTREME_HEAT', 'FOG']

export function DailyReportsPage() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [siteId, setSiteId] = useState('')
  const [status, setStatus] = useState<ReportStatus | ''>('')
  const [pendingOnly, setPendingOnly] = useState(false)
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(25)
  const [dialogOpen, setDialogOpen] = useState(false)

  const sites = useMySites()
  const query = useDailyReports({
    siteId: siteId || undefined,
    status,
    pendingApproval: pendingOnly || undefined,
    page,
    size,
  })

  return (
    <>
      <PageHeader
        title="Daily site execution reports"
        subtitle="One report per site, per date, per shift. Work, labour, materials and issues live inside it."
        actions={
          can('DAILY_REPORT:CREATE') && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              New report
            </Button>
          )
        }
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select
          label="Site"
          value={siteId}
          onChange={(e) => {
            setSiteId(e.target.value)
            setPage(0)
          }}
          sx={{ minWidth: 240 }}
        >
          <MenuItem value="">All sites</MenuItem>
          {(sites.data ?? []).map((s) => (
            <MenuItem key={s.id} value={s.id}>
              {s.code} - {s.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Status"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ReportStatus | '')
            setPage(0)
          }}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace('_', ' ')}
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          control={
            <Switch
              checked={pendingOnly}
              onChange={(e) => {
                setPendingOnly(e.target.checked)
                setPage(0)
              }}
            />
          }
          label="Awaiting my review"
        />
      </Paper>

      {query.isLoading && <LoadingBlock />}
      {query.error && <ErrorBlock error={query.error} />}

      {query.data && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Report no.</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Shift</TableCell>
                <TableCell>Site</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Engineer</TableCell>
                <TableCell align="right">Progress</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.content.map((report) => (
                <TableRow
                  key={report.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/daily-reports/${report.id}`)}
                >
                  <TableCell sx={{ fontFamily: 'monospace' }}>{report.reportNumber}</TableCell>
                  <TableCell>{formatDate(report.reportDate)}</TableCell>
                  <TableCell>{report.shift}</TableCell>
                  <TableCell>{report.siteCode ?? '-'}</TableCell>
                  <TableCell>{report.projectName ?? '-'}</TableCell>
                  <TableCell>{report.siteEngineerName ?? '-'}</TableCell>
                  <TableCell align="right">
                    {report.overallProgressPct != null ? `${report.overallProgressPct}%` : '-'}
                  </TableCell>
                  <TableCell>
                    <ReportStatusChip status={report.status} />
                  </TableCell>
                </TableRow>
              ))}
              {query.data.content.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No reports yet. Create one to start recording a day.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={query.data.page.totalElements}
            page={query.data.page.number}
            rowsPerPage={query.data.page.size}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(e) => {
              setSize(Number(e.target.value))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      <NewReportDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        sites={(sites.data ?? []).map((s) => ({ id: s.id, label: `${s.code} - ${s.name}` }))}
        defaultSiteId={siteId}
      />
    </>
  )
}

function NewReportDialog({
  open,
  onClose,
  sites,
  defaultSiteId,
}: {
  open: boolean
  onClose: () => void
  sites: { id: string; label: string }[]
  defaultSiteId: string
}) {
  const navigate = useNavigate()
  const create = useCreateDailyReport()
  const [form, setForm] = useState({
    siteId: defaultSiteId || (sites[0]?.id ?? ''),
    reportDate: new Date().toISOString().slice(0, 10),
    shift: 'DAY' as Shift,
    weather: '' as Weather | '',
    todayPlannedWork: '',
  })

  async function handleSave() {
    try {
      const created = await create.mutateAsync(form)
      onClose()
      navigate(`/daily-reports/${created.id}`)
    } catch {
      // surfaced from the mutation state
    }
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>New daily report</DialogTitle>
      <DialogContent dividers>
        {create.error && (
          <Box sx={{ mb: 2 }}>
            <ErrorBlock error={create.error} title="Could not create the report" />
          </Box>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A site can have only one report per date and shift. The report number is generated for you.
        </Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          <TextField
            select
            label="Site"
            required
            value={form.siteId}
            onChange={(e) => setForm({ ...form, siteId: e.target.value })}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          >
            {sites.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Report date"
            type="date"
            required
            value={form.reportDate}
            onChange={(e) => setForm({ ...form, reportDate: e.target.value })}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            select
            label="Shift"
            value={form.shift}
            onChange={(e) => setForm({ ...form, shift: e.target.value as Shift })}
          >
            {(['DAY', 'NIGHT', 'GENERAL'] as Shift[]).map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Weather"
            value={form.weather}
            onChange={(e) => setForm({ ...form, weather: e.target.value as Weather | '' })}
          >
            <MenuItem value="">Not recorded</MenuItem>
            {WEATHER.map((w) => (
              <MenuItem key={w} value={w}>
                {w.replace('_', ' ').toLowerCase()}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Today's planned work"
            value={form.todayPlannedWork}
            onChange={(e) => setForm({ ...form, todayPlannedWork: e.target.value })}
            multiline
            minRows={2}
            sx={{ gridColumn: { sm: '1 / -1' } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={create.isPending || !form.siteId || !form.reportDate}
        >
          {create.isPending ? 'Creating...' : 'Create and open'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function formatDate(value: string): string {
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}
